import type { WordCue } from "./types";
import { FrameParser } from "./frameParser";

export interface TTSClientOptions {
  /** Raw text to synthesize (raw markdown — ttsen strips what it can't speak). */
  input: string;
  /** Voice name from agent config (e.g. "af_heart"). */
  voice: string;
  /** Speaking speed, 0.5–2.0. Default 1.0. */
  speed?: number;
  /** Keycloak access token for bearer auth. */
  accessToken: string;
  /** AbortSignal for cancellation. */
  signal: AbortSignal;
  /** Called for each decoded audio frame. Payload is raw bytes (little-endian f32 PCM). */
  onAudio: (pcmBytes: Uint8Array) => void;
  /** Called for each decoded word-cues frame. */
  onWordCues: (cues: WordCue[]) => void;
}

/**
 * Send text to ttsen for speech synthesis using the framed transport
 * (PCM streaming + word timings). Calls onAudio/onWordCues as frames
 * arrive. Resolves when the stream is fully consumed. Rejects on
 * HTTP errors or if the signal is aborted.
 */
export async function synthesize(opts: TTSClientOptions): Promise<void> {
  const res = await fetch("/tts/v1/audio/speech", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${opts.accessToken}`,
    },
    body: JSON.stringify({
      input: opts.input,
      voice: opts.voice,
      speed: opts.speed ?? 1.0,
      response_format: "pcm",
      word_timings: true,
    }),
    signal: opts.signal,
  });

  if (!res.ok) {
    let message = `ttsen error ${res.status}`;
    try {
      const body = await res.json() as { error?: string };
      if (body.error) message = `ttsen error ${res.status}: ${body.error}`;
    } catch {
      // ignore JSON parse errors
    }
    throw new Error(message);
  }

  if (!res.body) {
    throw new Error("ttsen: no response stream");
  }

  const parser = new FrameParser();
  const reader = res.body.getReader();

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    const frames = parser.feed(value);
    for (const frame of frames) {
      if (frame.kind === "audio") {
        opts.onAudio(frame.payload);
      } else if (frame.kind === "words") {
        opts.onWordCues(frame.cues);
      }
    }
  }
}

export interface VoiceInfo {
  /** Raw voice ID (e.g. "af_heart") */
  id: string;
  /** Human-friendly label (e.g. "Heart (American Female)") */
  label: string;
  /** Category derived from prefix */
  category: "American Female" | "American Male" | "British Female" | "British Male" | "Unknown";
}

const PREFIX_TO_CATEGORY: Record<string, VoiceInfo["category"]> = {
  af: "American Female",
  am: "American Male",
  bf: "British Female",
  bm: "British Male",
};

/**
 * Fetch available voices from ttsen. No auth required.
 */
export async function fetchVoices(signal?: AbortSignal): Promise<VoiceInfo[]> {
  const res = await fetch("/tts/v1/voices", { signal });
  if (!res.ok) {
    throw new Error(`fetchVoices error ${res.status}`);
  }
  const body = await res.json() as { voices: string[] };

  const voices: VoiceInfo[] = body.voices.map((id) => {
    const underscoreIdx = id.indexOf("_");
    const prefix = underscoreIdx >= 0 ? id.slice(0, underscoreIdx) : "";
    const rest = underscoreIdx >= 0 ? id.slice(underscoreIdx + 1) : id;
    const category: VoiceInfo["category"] = PREFIX_TO_CATEGORY[prefix] ?? "Unknown";
    const name = rest.charAt(0).toUpperCase() + rest.slice(1);
    const label = `${name} (${category})`;
    return { id, label, category };
  });

  // Sort by category then label
  voices.sort((a, b) => {
    if (a.category < b.category) return -1;
    if (a.category > b.category) return 1;
    return a.label.localeCompare(b.label);
  });

  return voices;
}
